import React, { useMemo } from "react";
import { Stack, Text, Icon } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

interface Props { rows: RawIndicatorRow[] }

const PURVIEW_PURPLE = "#742774";
const PURVIEW_BG = "#FAF5FF";
const PURVIEW_BORDER = "#CE93D8";

const toNum = (v: string | number | undefined): number | null => {
  if (v === undefined || v === null || String(v).trim() === "") return null;
  const n = Number(String(v).replace(/[,%]/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

interface ParsedCommunity {
  depthCount: number | null;
  surveyCount: number | null;
  commCallCount: number | null;
  focusGroupCount: number | null;
  totalCount: number | null;
  weightDepth: string;
  weightSurvey: string;
  weightCommCall: string;
  weightFocusGroup: string;
  dynamicNote: string;
  activeSignals: string;
  scoringMethod: string;
  score: number | null;
}

function parse(rows: RawIndicatorRow[]): ParsedCommunity {
  const d: ParsedCommunity = {
    depthCount: null, surveyCount: null, commCallCount: null, focusGroupCount: null,
    totalCount: null, weightDepth: "40%", weightSurvey: "10%", weightCommCall: "20%",
    weightFocusGroup: "30%", dynamicNote: "", activeSignals: "", scoringMethod: "", score: null,
  };
  for (const row of rows) {
    const lbl = (row.factorLabel ?? "").trim();
    const val = String(row.value ?? "").trim();
    if (/Depth Engagement Count/i.test(lbl)) d.depthCount = toNum(row.value);
    else if (/Survey Count/i.test(lbl)) d.surveyCount = toNum(row.value);
    else if (/Community Call Count/i.test(lbl)) d.commCallCount = toNum(row.value);
    else if (/Focus Group Count/i.test(lbl)) d.focusGroupCount = toNum(row.value);
    else if (/^Total Count/i.test(lbl)) d.totalCount = toNum(row.value);
    else if (/Base Weight.*Depth/i.test(lbl)) d.weightDepth = val;
    else if (/Base Weight.*Survey/i.test(lbl)) d.weightSurvey = val;
    else if (/Base Weight.*Community Call/i.test(lbl)) d.weightCommCall = val;
    else if (/Base Weight.*Focus Group/i.test(lbl)) d.weightFocusGroup = val;
    else if (/dynamic re-adjustment/i.test(lbl)) d.dynamicNote = val;
    else if (/Active Signals/i.test(lbl)) d.activeSignals = val;
    else if (/Scoring Method/i.test(lbl)) d.scoringMethod = val;
    else if (/Community Engagement Score/i.test(lbl)) d.score = toNum(row.value);
  }
  return d;
}

interface ChannelBoxProps {
  icon: string;
  label: string;
  count: number | null;
  weight: string;
  accentColor: string;
  bgColor: string;
  borderColor: string;
}

const ChannelBox: React.FC<ChannelBoxProps> = ({ icon, label, count, weight, accentColor, bgColor, borderColor }) => (
  <Stack
    horizontalAlign="center"
    tokens={{ childrenGap: 4 }}
    styles={{
      root: {
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: 10,
        padding: "12px 10px",
        flex: "1 1 0",
        textAlign: "center",
        minWidth: 90,
      },
    }}
  >
    <Icon iconName={icon} styles={{ root: { fontSize: 18, color: accentColor } }} />
    <Text variant="xSmall" styles={{ root: { color: "#605E5C", lineHeight: "14px" } }}>{label}</Text>
    <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: accentColor } }}>
      {count !== null ? count : "—"}
    </Text>
    <Stack
      horizontalAlign="center"
      styles={{ root: { backgroundColor: accentColor, borderRadius: 8, padding: "1px 7px" } }}
    >
      <Text variant="xSmall" styles={{ root: { color: "#FFFFFF", fontWeight: 600 } }}>{weight}</Text>
    </Stack>
  </Stack>
);

const CHANNELS = [
  { key: "depth", icon: "ContactInfo", label: "Depth Engagement", weight: "weightDepth", count: "depthCount", accent: "#1565C0", bg: "#E3F2FD", border: "#90CAF9" },
  { key: "survey", icon: "OfflineOneDriveParachute", label: "Survey", weight: "weightSurvey", count: "surveyCount", accent: "#2E7D32", bg: "#E8F5E9", border: "#A5D6A7" },
  { key: "commCall", icon: "Phone", label: "Community Call", weight: "weightCommCall", count: "commCallCount", accent: "#BF6900", bg: "#FFF3E0", border: "#FFCC80" },
  { key: "focusGroup", icon: "People", label: "Focus Group", weight: "weightFocusGroup", count: "focusGroupCount", accent: "#5C2D91", bg: "#F3E5F5", border: "#CE93D8" },
];

const PurviewCommunityEngagementRawIndicatorCards: React.FC<Props> = ({ rows }) => {
  const d = useMemo(() => parse(rows), [rows]);

  const scoreColor =
    d.score === null ? "#323130"
      : d.score >= 71 ? "#107C10"
        : d.score >= 41 ? "#C15A00"
          : "#D13438";

  return (
    <Stack
      tokens={{ childrenGap: 16 }}
      styles={{
        root: {
          backgroundColor: PURVIEW_BG,
          border: `1.5px solid ${PURVIEW_BORDER}`,
          borderRadius: 12,
          padding: "20px 24px",
        },
      }}
    >
      {/* Header row */}
      <Stack horizontal verticalAlign="center" horizontalAlign="space-between" tokens={{ childrenGap: 12 }}>
        <Stack tokens={{ childrenGap: 2 }}>
          <Text variant="large" styles={{ root: { fontWeight: 700, color: PURVIEW_PURPLE } }}>
            Community Engagement
          </Text>
          {d.totalCount !== null && (
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              Total Engagements: {d.totalCount}
            </Text>
          )}
        </Stack>
        {/* Score pill */}
        <Stack
          horizontalAlign="center"
          verticalAlign="center"
          styles={{
            root: {
              backgroundColor: scoreColor,
              borderRadius: 20,
              padding: "6px 18px",
              minWidth: 56,
            },
          }}
        >
          <Text variant="large" styles={{ root: { fontWeight: 700, color: "#FFFFFF" } }}>
            {d.score !== null ? d.score : "—"}
          </Text>
        </Stack>
      </Stack>

      {/* Channel boxes */}
      <Stack horizontal tokens={{ childrenGap: 10 }} wrap>
        {CHANNELS.map((ch) => (
          <ChannelBox
            key={ch.key}
            icon={ch.icon}
            label={ch.label}
            count={(d as any)[ch.count] as number | null}
            weight={(d as any)[ch.weight] as string}
            accentColor={ch.accent}
            bgColor={ch.bg}
            borderColor={ch.border}
          />
        ))}
      </Stack>

      {/* Active signals & dynamic adjustment */}
      {(d.activeSignals || d.dynamicNote) && (
        <Stack tokens={{ childrenGap: 4 }}>
          {d.activeSignals && (
            <Text variant="small" styles={{ root: { color: "#323130" } }}>
              <span style={{ fontWeight: 600 }}>Active Signals: </span>{d.activeSignals}
            </Text>
          )}
          {d.dynamicNote && (
            <Text variant="small" styles={{ root: { color: "#605E5C", fontStyle: "italic" } }}>
              {d.dynamicNote}
            </Text>
          )}
        </Stack>
      )}

      {/* Scoring method */}
      {d.scoringMethod && (
        <>
          <div style={{ height: 1, backgroundColor: PURVIEW_BORDER }} />
          <Text variant="small" styles={{ root: { color: "#323130" } }}>
            <span style={{ fontWeight: 600 }}>Scoring Method: </span>{d.scoringMethod}
          </Text>
        </>
      )}
    </Stack>
  );
};

export default PurviewCommunityEngagementRawIndicatorCards;
